import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getUsers, updateUser, deleteUser, getAdminPeople, createAdminAccount, linkAdminAccount,
} from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import PageHeader from '../../components/PageHeader'
import RolesChecklist from '../../components/admin/RolesChecklist'
import { ROLES, ROLE_BADGE, roleBadge, userRoles, useRoleText } from '../../components/admin/roles'
import { detalleError } from '../../components/admin/detalle'

// Administración > Cuentas: the app's login accounts (models.AppUser), what
// used to be Configuración > Usuarios. An account can be made here before
// its owner first signs in (Entra adopts it then), and is linked to at most
// one person -- one account per person, the server answers 409 otherwise.

const DOMINIO = '@rebornelectric.cl'
const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200'
const labelCls = 'block text-xs font-medium text-gray-600 mb-1.5'
const initials = (name) => (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)

export default function AdminCuentas() {
  const { t } = useTranslation(['admin', 'common'])
  const { roleLabel, roleDesc } = useRoleText()
  const { user: me, hasRole } = useAuth()
  const soyAdmin = hasRole('admin')
  // RRHH manages accounts, never an admin's.
  const esDeAdmin = (u) => !soyAdmin && userRoles(u).includes('admin')
  const [users, setUsers] = useState([])
  const [personas, setPersonas] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [sinPersona, setSinPersona] = useState(false)
  const [modal, setModal] = useState(null)            // null | 'new' | account | { personaId } (new, for that person)

  const load = async () => {
    setLoading(true)
    try {
      const [u, p] = await Promise.all([getUsers(), getAdminPeople({ include_inactive: true })])
      setUsers(u)
      setPersonas(Array.isArray(p) ? p : [])
    } catch {
      setError(t('cuentas.loadFailed'))
    }
    setLoading(false)
  }

  useEffect(() => { load() }, [])   // eslint-disable-line react-hooks/exhaustive-deps

  const personaPorId = useMemo(() => new Map(personas.map(p => [p.id, p])), [personas])
  // AppUserOut doesn't say whether the account was ever signed in to; the
  // person it's linked to does (cuenta.ha_ingresado). Unlinked: unknown.
  const nuncaIngreso = (u) => {
    const c = u.team_member_id ? personaPorId.get(u.team_member_id)?.cuenta : null
    return c?.id === u.id && !c.ha_ingresado
  }
  const visibles = sinPersona ? users.filter(u => u.team_member_id == null) : users
  const nSinPersona = users.filter(u => u.team_member_id == null).length
  // Active people nobody can sign in as: what a permission by person, cargo or area head cannot reach.
  const sinCuenta = personas.filter(p => p.is_active && !p.es_equipo && !p.cuenta)

  const handleToggleActive = async (u) => {
    if (u.id === me.id) return
    setError('')
    try {
      await updateUser(u.id, { is_active: !u.is_active })
      await load()
    } catch (e) {
      setError(detalleError(e, t('cuentas.saveFailed')))
    }
  }

  const handleDelete = async (u) => {
    if (!confirm(t('cuentas.deleteConfirm', { name: u.display_name }))) return
    setError('')
    try {
      await deleteUser(u.id)
      await load()
    } catch (e) {
      setError(detalleError(e, t('cuentas.deleteFailed')))
    }
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <PageHeader title={t('cuentas.title')} subtitle={t('cuentas.subtitle')}
          actions={(
            <button onClick={() => setModal('new')}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">
              {t('cuentas.add')}
            </button>
          )} />
      </div>

      <p className="text-xs text-gray-400 mb-4">{t('cuentas.managedByEntra')}</p>

      {/* Roles legend */}
      <div className="flex gap-4 mb-5 flex-wrap">
        {ROLES.map(r => (
          <div key={r.value} className="flex items-center gap-2 text-xs text-gray-500">
            <span className={`px-2 py-0.5 rounded-full font-medium ${ROLE_BADGE[r.value]}`}>{roleLabel(r.value)}</span>
            <span>{roleDesc(r.value)}</span>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-3">
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={sinPersona} onChange={e => setSinPersona(e.target.checked)} className="accent-gray-900" />
          {t('cuentas.filtroSinPersona', { count: nSinPersona })}
        </label>
      </div>

      {error && <p className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <p className="text-sm text-gray-400 px-6 py-8">{t('common:actions.loading')}</p>
        ) : visibles.length === 0 ? (
          <p className="text-sm text-gray-400 px-6 py-8">{sinPersona ? t('cuentas.emptySinPersona') : t('cuentas.empty')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {['user', 'email', 'persona', 'roles', 'status'].map(c => (
                  <th key={c} className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{t(`cuentas.columns.${c}`)}</th>
                ))}
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {visibles.map(u => {
                const rs = userRoles(u)
                const persona = u.team_member_id ? personaPorId.get(u.team_member_id) : null
                return (
                  <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors last:border-b-0">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-900 flex items-center justify-center shrink-0">
                          <span className="text-xs font-semibold text-white">{initials(u.display_name)}</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{u.display_name}</p>
                          {u.id === me.id && <p className="text-[10px] text-gray-400">{t('cuentas.you')}</p>}
                          {nuncaIngreso(u) && <p className="text-[10px] text-amber-600 font-medium">{t('cuentas.nuncaIngreso')}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-gray-500">{u.email || <span className="text-gray-300">—</span>}</td>
                    <td className="px-5 py-3 text-gray-500">
                      {u.team_member_id
                        ? persona?.name ?? <span className="text-gray-300">#{u.team_member_id}</span>
                        : <span className="text-amber-500 text-xs font-medium">{t('cuentas.notLinked')}</span>}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        {rs.length ? rs.map(r => (
                          <span key={r} className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleBadge(r)}`}>
                            {roleLabel(r)}
                          </span>
                        )) : <span className="text-xs text-gray-300">—</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => handleToggleActive(u)}
                        disabled={u.id === me.id || esDeAdmin(u)}
                        title={u.id === me.id ? t('cuentas.cannotDisableSelf') : undefined}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${u.is_active ? 'bg-emerald-500' : 'bg-gray-300'} ${u.id === me.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${u.is_active ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
                      </button>
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setModal(u)} disabled={esDeAdmin(u)} className="text-xs text-gray-400 hover:text-gray-900 mr-3 disabled:opacity-30 disabled:cursor-not-allowed">{t('common:actions.edit')}</button>
                      <button
                        onClick={() => handleDelete(u)}
                        disabled={u.id === me.id || esDeAdmin(u)}
                        className="text-xs text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        {t('common:actions.delete')}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {!loading && (
        <div className="mt-6 bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-800">{t('cuentas.sinCuentaTitulo', { count: sinCuenta.length })}</h2>
          <p className="text-xs text-gray-500 mb-3">{t('cuentas.sinCuentaAyuda')}</p>
          {sinCuenta.length === 0 ? <p className="text-xs text-gray-400">{t('cuentas.sinCuentaVacio')}</p> : (
            <ul className="divide-y divide-gray-50 text-sm">
              {sinCuenta.map(p => (
                <li key={p.id} className="flex items-center justify-between py-1.5">
                  <span className="text-gray-800">{p.name}{p.role ? <span className="text-xs text-gray-400"> · {p.role}</span> : null}</span>
                  <button onClick={() => setModal({ personaId: p.id })} className="text-xs text-gray-500 hover:text-gray-900">{t('cuentas.crearCuenta')}</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {modal && (
        <CuentaModal
          cuenta={modal === 'new' || modal.personaId ? null : modal}
          personaInicial={modal.personaId}
          personas={personas}
          onClose={() => setModal(null)}
          onSaved={async () => { setModal(null); await load() }}
          onPartial={load}
        />
      )}
    </div>
  )
}

// New account (email, name, roles, maybe a person) or an existing one's edit.
// The person link goes through /vinculo, so "that person already has an
// account" comes back as a 409 shown here.
function CuentaModal({ cuenta, personaInicial, personas, onClose, onSaved, onPartial }) {
  const { t } = useTranslation(['admin', 'common'])
  const [form, setForm] = useState(() => {
    const p = personaInicial ? personas.find(x => x.id === personaInicial) : null
    return {
      display_name: cuenta?.display_name || p?.name || '',
      email: cuenta?.email || p?.email || '',
      roles: cuenta ? userRoles(cuenta) : ['engineer'],
      team_member_id: cuenta?.team_member_id ?? personaInicial ?? '',
    }
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Picking a person for a new account fills in what's still empty.
  const elegirPersona = (id) => setForm(f => {
    const p = personas.find(x => x.id === Number(id))
    if (cuenta || !p) return { ...f, team_member_id: id }
    return { ...f, team_member_id: id, display_name: f.display_name || p.name, email: f.email || p.email || '' }
  })

  const handleSave = async () => {
    const email = form.email.trim().toLowerCase()
    if (!form.display_name.trim()) { setError(t('cuentas.form.displayNameRequired')); return }
    if (form.roles.length === 0) { setError(t('cuentas.form.rolesRequired')); return }
    if (!cuenta && !email.endsWith(DOMINIO)) { setError(t('cuentas.form.emailDominio', { dominio: DOMINIO })); return }
    const personaId = form.team_member_id ? Number(form.team_member_id) : null
    setSaving(true)
    setError('')
    try {
      if (!cuenta) {
        await createAdminAccount({ email, display_name: form.display_name.trim(), roles: form.roles, team_member_id: personaId })
      } else {
        await updateUser(cuenta.id, { display_name: form.display_name.trim(), email: email || null, roles: form.roles })
        if (personaId !== (cuenta.team_member_id ?? null)) {
          try {
            await linkAdminAccount(cuenta.id, personaId)
          } catch (e) {
            // The rest was saved; only the link wasn't.
            onPartial()
            setError(detalleError(e, t('cuentas.saveFailed')))
            setSaving(false)
            return
          }
        }
      }
      await onSaved()
    } catch (e) {
      setError(detalleError(e, t('cuentas.saveFailed')))
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-5">
          {cuenta ? t('cuentas.form.editTitle', { name: cuenta.display_name }) : t('cuentas.form.addTitle')}
        </h2>

        {error && (
          <p className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>
        )}

        <div className="space-y-4">
          <div>
            <label className={labelCls}>{t('cuentas.form.email')}{cuenta ? '' : ' *'}</label>
            <input type="email" autoFocus={!cuenta} className={inputCls} value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder={cuenta ? t('cuentas.form.emailPlaceholder') : `nombre${DOMINIO}`} />
            {!cuenta && <p className="text-[11px] text-gray-400 mt-1">{t('cuentas.form.emailHint')}</p>}
          </div>

          <div>
            <label className={labelCls}>{t('cuentas.form.displayName')} *</label>
            <input autoFocus={!!cuenta} className={inputCls} value={form.display_name}
              onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} />
          </div>

          <div>
            <label className={labelCls}>{t('cuentas.form.persona')}</label>
            <select className={inputCls} value={form.team_member_id} onChange={e => elegirPersona(e.target.value)}>
              <option value="">{t('cuentas.form.sinPersona')}</option>
              {personas.map(p => {
                const otra = p.cuenta && p.cuenta.id !== cuenta?.id
                return (
                  <option key={p.id} value={p.id}>
                    {p.name}{!p.is_active ? ` (${t('personas.inactivo')})` : ''}{otra ? ` — ${t('cuentas.form.yaTieneCuenta')}` : ''}
                  </option>
                )
              })}
            </select>
            <p className="text-[11px] text-gray-400 mt-1">{t('cuentas.form.personaHint')}</p>
          </div>

          <div>
            <label className={labelCls}>{t('cuentas.form.roles')} *</label>
            <RolesChecklist value={form.roles} onChange={roles => setForm(f => ({ ...f, roles }))} />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-gray-900 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-40 transition-colors"
          >
            {saving ? t('common:actions.saving') : cuenta ? t('cuentas.form.saveChanges') : t('cuentas.form.create')}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </div>
  )
}
